import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const BACKEND = "https://localrush-backend-production.up.railway.app";

let stripePromiseCache = null;
function getStripePromise() {
  if (stripePromiseCache) return stripePromiseCache;
  stripePromiseCache = fetch(`${BACKEND}/api/config`)
    .then(r => r.json())
    .then(cfg => loadStripe(cfg.stripePublishableKey));
  return stripePromiseCache;
}

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700;9..144,900&family=Geist:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

    :root {
      --cream: #FAF7F2;
      --cream-2: #F5F0E8;
      --ink: #0F0F10;
      --ink-soft: #2C2C30;
      --ink-muted: #6B6B70;
      --ink-faint: #A8A8AD;
      --line: #E8E4DC;
      --orange: #F25C12;
      --orange-deep: #D4420B;
      --orange-tint: #FEE8DB;
      --green: #0B7A3E;
    }

    * { box-sizing: border-box; }
    html, body, #root { margin: 0; padding: 0; background: var(--cream); color: var(--ink); }
    body { font-family: 'Geist', -apple-system, sans-serif; font-feature-settings: 'ss01', 'ss02'; -webkit-font-smoothing: antialiased; }

    .serif { font-family: 'Fraunces', serif; font-optical-sizing: auto; letter-spacing: -0.02em; }
    .mono { font-family: 'JetBrains Mono', monospace; font-feature-settings: 'tnum'; }

    .mesh-bg { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
    .mesh-blob { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.55; will-change: transform; }
    .mesh-blob-1 { width: 650px; height: 650px; background: radial-gradient(circle, #FFB088 0%, #F25C12 40%, transparent 70%); top: -150px; left: -100px; animation: float1 22s ease-in-out infinite; }
    .mesh-blob-2 { width: 550px; height: 550px; background: radial-gradient(circle, #FFD9B8 0%, #FFA060 45%, transparent 70%); top: 10%; right: -80px; animation: float2 28s ease-in-out infinite; }
    .mesh-blob-3 { width: 500px; height: 500px; background: radial-gradient(circle, #FDE4C9 0%, #F7C89D 50%, transparent 70%); bottom: -120px; left: 30%; animation: float3 25s ease-in-out infinite; opacity: 0.4; }

    @keyframes float1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(80px,60px) scale(1.08); } 66% { transform: translate(-40px,90px) scale(0.95); } }
    @keyframes float2 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-100px,80px) scale(1.12); } }
    @keyframes float3 { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(-70px,-60px) scale(1.05); } 75% { transform: translate(60px,-30px) scale(0.92); } }

    .grain { position: absolute; inset: 0; pointer-events: none; z-index: 1; opacity: 0.035; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' /%3E%3C/svg%3E"); }

    @keyframes reveal { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
    .reveal { animation: reveal 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }
    .reveal-1 { animation-delay: 0.05s; }
    .reveal-2 { animation-delay: 0.15s; }
    .reveal-3 { animation-delay: 0.25s; }
    .reveal-4 { animation-delay: 0.35s; }
    .reveal-5 { animation-delay: 0.45s; }

    .lift { transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s; }
    .lift:hover { transform: translateY(-4px); box-shadow: 0 20px 40px -20px rgba(15,15,16,0.18); }

    @keyframes dash-flow { to { stroke-dashoffset: -30; } }
    .dash-flow { animation: dash-flow 1.5s linear infinite; }

    .btn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--ink); color: var(--cream); padding: 14px 26px; border-radius: 999px; border: none; font-family: 'Geist', sans-serif; font-weight: 500; font-size: 15px; cursor: pointer; letter-spacing: -0.01em; transition: transform 0.2s, background 0.2s; }
    .btn-primary:hover:not(:disabled) { transform: translateY(-1px); background: var(--orange); }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

    .btn-secondary { display: inline-flex; align-items: center; gap: 8px; background: transparent; color: var(--ink); padding: 13px 24px; border-radius: 999px; border: 1px solid var(--ink); font-family: 'Geist', sans-serif; font-weight: 500; font-size: 15px; cursor: pointer; letter-spacing: -0.01em; transition: background 0.2s, color 0.2s; }
    .btn-secondary:hover { background: var(--ink); color: var(--cream); }

    .ul-link { background-image: linear-gradient(currentColor, currentColor); background-size: 0% 1px; background-repeat: no-repeat; background-position: 0 100%; transition: background-size 0.3s; }
    .ul-link:hover { background-size: 100% 1px; }

    @media (max-width: 768px) {
      .hide-mobile { display: none !important; }
      .show-mobile { display: flex !important; }
      .mobile-menu.show-mobile { display: flex !important; flex-direction: column !important; }

      /* Tighten horizontal padding everywhere */
      .mobile-tight-padding { padding-left: 20px !important; padding-right: 20px !important; }
      .mobile-tight-padding-large { padding-left: 20px !important; padding-right: 20px !important; padding-top: 80px !important; padding-bottom: 60px !important; }

      /* Stack any grid into single column on mobile */
      .mobile-stack { grid-template-columns: 1fr !important; gap: 32px !important; }
      .mobile-stack-tight { grid-template-columns: 1fr !important; gap: 16px !important; }

      /* Hero stats row: 2 columns instead of 4 on mobile */
      .mobile-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 16px !important; }
      .mobile-stat-item {
        padding-left: 0 !important;
        padding-right: 0 !important;
        border-right: none !important;
        border-bottom: 1px solid var(--line);
        padding-bottom: 16px;
      }

      /* Who-we-serve: always 2 columns max on mobile */
      .mobile-who-grid { grid-template-columns: repeat(2, 1fr) !important; }

      /* Comparison table: stack rows vertically on mobile */
      .mobile-comp-row {
        grid-template-columns: 1fr !important;
        gap: 8px !important;
      }
      .mobile-comp-row > :last-child { grid-column: 1 / -1; }

      /* Pricing scope card: stack on mobile */
      .mobile-scope-grid { grid-template-columns: 1fr !important; gap: 24px !important; }

      /* Footer: stack columns on mobile */
      .mobile-footer-grid { grid-template-columns: 1fr !important; gap: 32px !important; }

      /* Mobile menu drawer */
      .mobile-menu {
        position: fixed;
        top: 64px;
        left: 0; right: 0;
        background: var(--cream);
        border-bottom: 1px solid var(--line);
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        z-index: 99;
      }
      .mobile-menu button {
        text-align: left;
        padding: 14px 16px !important;
        font-size: 16px !important;
        border-radius: 8px;
      }
      .mobile-menu button:hover { background: var(--cream-2) !important; }

      /* Reduce huge headings so they fit */
      .mobile-hero-title { font-size: 56px !important; line-height: 0.95 !important; }
      .mobile-section-title { font-size: 40px !important; }
      .mobile-cta-title { font-size: 52px !important; }

      /* Section vertical padding reduction */
      .mobile-section { padding-top: 70px !important; padding-bottom: 70px !important; }

      /* Booking form card: tighter padding on mobile */
      .mobile-book-card { padding: 28px 20px !important; }

      /* Disable sticky positioning on mobile (fixes How It Works overlap) */
      .mobile-unsticky { position: static !important; top: auto !important; }
    }
    .show-mobile { display: none; }
  `}</style>
);

function Logo({ size = 32, textSize = 18, color = "ink" }) {
  const fill = color === "ink" ? "var(--ink)" : "var(--cream)";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <rect x="2" y="8" width="24" height="22" rx="3" fill="var(--ink)" />
        <rect x="2" y="8" width="24" height="5" rx="3" fill="var(--orange)" />
        <path d="M26 19 L36 19 M32 14 L37 19 L32 24" stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
      <span className="serif" style={{ fontSize: textSize, fontWeight: 600, color: fill, letterSpacing: "-0.03em", lineHeight: 1 }}>
        Local<span style={{ color: "var(--orange)" }}>Rush</span>
      </span>
    </div>
  );
}

function Nav({ active, setActive }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  // Close mobile menu when changing pages
  function go(page) {
    setActive(page);
    setMobileOpen(false);
  }

  return (
    <>
    <nav className="mobile-tight-padding" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled || mobileOpen ? "rgba(250,247,242,0.95)" : "transparent",
      backdropFilter: scrolled || mobileOpen ? "blur(20px) saturate(180%)" : "none",
      borderBottom: scrolled || mobileOpen ? "1px solid var(--line)" : "1px solid transparent",
      padding: "18px 40px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      transition: "all 0.3s",
    }}>
      <button onClick={() => go("Home")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
        <Logo size={28} textSize={20} />
      </button>

      {/* Desktop nav */}
      <div className="hide-mobile" style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {["Home", "Pricing", "About"].map(item => (
          <button key={item} onClick={() => go(item)}
            style={{
              background: "transparent", border: "none",
              color: active === item ? "var(--ink)" : "var(--ink-muted)",
              padding: "8px 14px", fontSize: 14, fontWeight: active === item ? 500 : 400,
              fontFamily: "'Geist', sans-serif", cursor: "pointer", letterSpacing: "-0.01em",
            }}>{item}</button>
        ))}
        <button onClick={() => go("Book Now")} className="btn-primary" style={{ marginLeft: 12, padding: "10px 22px", fontSize: 14 }}>
          Book now
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>

      {/* Mobile hamburger */}
      <button
        className="show-mobile"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
        style={{
          display: "none",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 8,
          width: 40, height: 40,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
        }}
      >
        <span style={{
          width: 22, height: 2, background: "var(--ink)",
          transform: mobileOpen ? "rotate(45deg) translate(3px, 4px)" : "none",
          transition: "transform 0.2s",
        }}/>
        <span style={{
          width: 22, height: 2, background: "var(--ink)",
          opacity: mobileOpen ? 0 : 1,
          transition: "opacity 0.2s",
        }}/>
        <span style={{
          width: 22, height: 2, background: "var(--ink)",
          transform: mobileOpen ? "rotate(-45deg) translate(3px, -4px)" : "none",
          transition: "transform 0.2s",
        }}/>
      </button>
    </nav>

    {/* Mobile menu drawer */}
    {mobileOpen && (
      <div className="mobile-menu show-mobile" style={{ display: "none" }}>
        {["Home", "Pricing", "About"].map(item => (
          <button key={item} onClick={() => go(item)}
            style={{
              background: "transparent", border: "none",
              color: active === item ? "var(--orange)" : "var(--ink)",
              fontWeight: active === item ? 500 : 400,
              fontFamily: "'Geist', sans-serif", cursor: "pointer",
            }}>{item}</button>
        ))}
        <button onClick={() => go("Book Now")}
          style={{
            background: "var(--ink)", border: "none",
            color: "var(--cream)",
            fontFamily: "'Geist', sans-serif", cursor: "pointer",
            marginTop: 8, fontWeight: 500,
          }}>Book a delivery →</button>
      </div>
    )}
    </>
  );
}

function HomePage({ setActive }) {
  return (
    <div>
      {/* HERO */}
      <section className="mobile-tight-padding-large" style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: "140px 40px 80px", background: "var(--cream)", position: "relative", overflow: "hidden" }}>
        <div className="mesh-bg">
          <div className="mesh-blob mesh-blob-1" />
          <div className="mesh-blob mesh-blob-2" />
          <div className="mesh-blob mesh-blob-3" />
        </div>
        <div className="grain" />

        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2, width: "100%" }}>
          <div className="reveal reveal-1" style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)",
            border: "1px solid var(--line)",
            borderRadius: 999, padding: "6px 14px 6px 10px", marginBottom: 40,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: "var(--green)",
              boxShadow: "0 0 0 3px rgba(11,122,62,0.15)",
              animation: "pulse 2s ease-in-out infinite",
            }} />
            <span style={{ fontSize: 12, color: "var(--ink-soft)", letterSpacing: "-0.01em", fontWeight: 500 }}>
              Dispatching deliveries now · Wasatch Front
            </span>
          </div>
          <style>{`@keyframes pulse { 0%,100% { box-shadow: 0 0 0 3px rgba(11,122,62,0.15); } 50% { box-shadow: 0 0 0 6px rgba(11,122,62,0.05); } }`}</style>

          <h1 className="serif reveal reveal-2 mobile-hero-title" style={{ fontSize: "clamp(56px, 9vw, 128px)", fontWeight: 400, color: "var(--ink)", lineHeight: 0.92, margin: "0 0 32px", maxWidth: 1000, letterSpacing: "-0.04em" }}>
            Local delivery.<br />
            <span style={{ fontStyle: "italic", fontWeight: 300, color: "var(--orange)" }}>Consider it done.</span>
          </h1>

          <p className="reveal reveal-3" style={{ color: "var(--ink-soft)", fontSize: "clamp(17px, 1.7vw, 22px)", fontWeight: 400, lineHeight: 1.5, margin: "0 0 48px", maxWidth: 620, letterSpacing: "-0.01em" }}>
            Faster than sending staff. Cheaper than overnight. Same-day courier service built for Salt Lake City businesses — booked in about a minute.
          </p>

          <div className="reveal reveal-4" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 80 }}>
            <button onClick={() => setActive("Book Now")} className="btn-primary">
              Book a delivery
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button onClick={() => setActive("Pricing")} className="btn-secondary">See pricing</button>
          </div>

          <div className="reveal reveal-5 mobile-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 0, borderTop: "1px solid var(--line)", paddingTop: 32, maxWidth: 900 }}>
            {[
              { num: "$29", label: "Starting price", sub: "Planned tier" },
              { num: "<1 hr", label: "Rush delivery", sub: "Fastest tier" },
              { num: "60 sec", label: "To book", sub: "Online" },
              { num: "SLC", label: "All of the valley", sub: "2026 · live" },
            ].map((s, i) => (
              <div key={i} className="mobile-stat-item" style={{ paddingRight: 24, borderRight: i < 3 ? "1px solid var(--line)" : "none", paddingLeft: i > 0 ? 24 : 0 }}>
                <div className="serif" style={{ fontSize: 40, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.03em", lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 8, fontWeight: 500 }}>{s.label}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mobile-tight-padding mobile-section" style={{ background: "var(--cream)", padding: "120px 40px", position: "relative" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="mobile-stack" style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 80, alignItems: "start" }}>
            <div className="mobile-unsticky" style={{ position: "sticky", top: 120 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16, fontWeight: 500 }}>
                01 — How it works
              </div>
              <h2 className="serif mobile-section-title" style={{ fontSize: "clamp(36px, 4.5vw, 56px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.03em", lineHeight: 1, margin: "0 0 12px" }}>
                Three steps.<br />
                <span style={{ fontStyle: "italic", color: "var(--ink-muted)" }}>No friction.</span>
              </h2>
            </div>

            <div style={{ position: "relative" }}>
              <svg style={{ position: "absolute", left: 24, top: 16, height: "calc(100% - 40px)", width: 2 }} preserveAspectRatio="none" viewBox="0 0 2 100">
                <line x1="1" y1="0" x2="1" y2="100" stroke="var(--orange)" strokeWidth="2" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" className="dash-flow" />
              </svg>

              {[
                { n: "01", title: "Book online", body: "Enter pickup and dropoff, pick a delivery speed, pay by card. The whole form takes about a minute." },
                { n: "02", title: "Driver dispatched", body: "A vetted courier is assigned automatically within minutes. You get confirmation immediately." },
                { n: "03", title: "Live tracking", body: "You and your recipient both get a live tracking link. Updates at every milestone." },
              ].map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 32, marginBottom: i < 2 ? 64 : 0, alignItems: "start" }}>
                  <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: "50%", background: "var(--ink)", color: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 500, position: "relative", zIndex: 1 }}>{step.n}</div>
                  <div style={{ paddingTop: 8 }}>
                    <h3 className="serif" style={{ fontSize: 28, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 10px" }}>{step.title}</h3>
                    <p style={{ fontSize: 17, color: "var(--ink-soft)", lineHeight: 1.55, margin: 0, maxWidth: 460 }}>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="mobile-tight-padding mobile-section" style={{ background: "var(--ink)", padding: "120px 40px", color: "var(--cream)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16, fontWeight: 500 }}>
            02 — Who we serve
          </div>
          <h2 className="serif mobile-section-title" style={{ fontSize: "clamp(36px, 5vw, 64px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, margin: "0 0 64px", maxWidth: 800 }}>
            Built for the businesses that keep<br />
            <span style={{ fontStyle: "italic", color: "var(--orange)" }}>this valley running.</span>
          </h2>

          <div className="mobile-who-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.08)" }}>
            {[
              { label: "Print & sign shops", desc: "Business cards, banners, proofs, rush jobs" },
              { label: "Gift & specialty shops", desc: "Gift baskets, gourmet boxes, corporate gifts" },
              { label: "Florists", desc: "Arrangements, plants, scheduled deliveries" },
              { label: "Law firms", desc: "Document copies, filings, discovery materials" },
              { label: "Dental offices", desc: "Supplies, referrals, non-patient documents" },
              { label: "Accounting firms", desc: "Tax documents, returns, client paperwork" },
              { label: "Real estate offices", desc: "Keys, yard signs, document copies" },
              { label: "Bakeries & caterers", desc: "Pastries, gift boxes, platters (no custom cakes)" },
              { label: "Any local business", desc: "Documents, supplies, items under $500" },
            ].map((item, i) => (
              <div key={i} style={{ background: "var(--ink)", padding: "36px 32px", transition: "background 0.3s" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#1A1A1D"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--ink)"}>
                <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--cream)", letterSpacing: "-0.02em", marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontSize: 14, color: "rgba(250,247,242,0.55)", lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="mobile-tight-padding mobile-section" style={{ background: "var(--cream)", padding: "120px 40px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16, fontWeight: 500 }}>
            03 — The math
          </div>
          <h2 className="serif mobile-section-title" style={{ fontSize: "clamp(36px, 4.5vw, 56px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.03em", lineHeight: 1, margin: "0 0 48px", maxWidth: 800 }}>
            Same-day beats next-day.<br />
            <span style={{ fontStyle: "italic", color: "var(--ink-muted)" }}>Every single time.</span>
          </h2>

          <div style={{ borderTop: "1px solid var(--line)" }}>
            {[
              { opt: "Sending your staff", arrives: "Whenever", detail: "Pulls someone off their actual job · Lost productivity · Gas", outcome: "avoid" },
              { opt: "FedEx overnight", arrives: "Tomorrow", detail: "Next-day delivery · Even for a same-city move", outcome: "avoid" },
              { opt: "UPS Next Day Air", arrives: "Tomorrow", detail: "Same as FedEx · Still takes until tomorrow afternoon", outcome: "avoid" },
              { opt: "LocalRush Planned", arrives: "Your window", detail: "You pick a 2-hour window · Dispatched to hit it", outcome: "yes" },
              { opt: "LocalRush Standard", arrives: "1–3 hours", detail: "Same-day · Dispatched the moment you book", outcome: "yes" },
              { opt: "LocalRush Rush", arrives: "Under 1 hr", detail: "Priority dispatch · For genuine emergencies", outcome: "yes" },
            ].map((row, i) => (
              <div key={i} className="mobile-comp-row" style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 2fr",
                gap: 24,
                alignItems: "center",
                padding: "28px 0",
                borderBottom: "1px solid var(--line)",
                opacity: row.outcome === "avoid" ? 0.55 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: row.outcome === "yes" ? "var(--orange)" : "var(--ink-faint)", flexShrink: 0 }} />
                  <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", textDecoration: row.outcome === "avoid" ? "line-through" : "none" }}>
                    {row.opt}
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 14, color: row.outcome === "yes" ? "var(--orange)" : "var(--ink-soft)", fontWeight: 500, textAlign: "right", minWidth: 110, textTransform: "uppercase", letterSpacing: "0.04em" }}>{row.arrives}</div>
                <div style={{ fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.5 }}>{row.detail}</div>
              </div>
            ))}
          </div>

          <p style={{ marginTop: 32, fontSize: 13, color: "var(--ink-faint)", fontStyle: "italic", lineHeight: 1.5, maxWidth: 680 }}>
            Pricing is comparable to overnight shipping. The difference is when it arrives.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mobile-tight-padding mobile-section" style={{ background: "var(--orange)", padding: "120px 40px", position: "relative", overflow: "hidden" }}>
        <div className="grain" />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2, textAlign: "center" }}>
          <h2 className="serif mobile-cta-title" style={{ fontSize: "clamp(48px, 7vw, 96px)", fontWeight: 400, color: "var(--cream)", letterSpacing: "-0.04em", lineHeight: 0.95, margin: "0 0 32px" }}>
            Move something<br />
            <span style={{ fontStyle: "italic" }}>right now.</span>
          </h2>
          <p style={{ color: "rgba(250,247,242,0.9)", fontSize: 18, margin: "0 0 40px", lineHeight: 1.5 }}>
            No signup. No contracts. Book your first delivery in about a minute.
          </p>
          <button onClick={() => setActive("Book Now")} style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "var(--ink)", color: "var(--cream)",
            padding: "18px 36px", borderRadius: 999, border: "none",
            fontFamily: "'Geist', sans-serif", fontWeight: 500, fontSize: 16,
            cursor: "pointer", letterSpacing: "-0.01em",
          }}>
            Book a delivery
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </section>
    </div>
  );
}

// ── PRICING PAGE ─────────────────────────────────────────────────────────────
function PricingPage({ setActive }) {
  return (
    <div className="mobile-tight-padding-large" style={{ background: "var(--cream)", minHeight: "100vh", padding: "140px 40px 100px", position: "relative", overflow: "hidden" }}>
      <div className="mesh-bg" style={{ opacity: 0.5 }}>
        <div className="mesh-blob mesh-blob-2" />
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div className="mono reveal reveal-1" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16, fontWeight: 500 }}>
            Pricing
          </div>
          <h1 className="serif reveal reveal-2 mobile-hero-title" style={{ fontSize: "clamp(48px, 6vw, 80px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.04em", lineHeight: 0.95, margin: "0 0 24px" }}>
            Three ways to <span style={{ fontStyle: "italic", color: "var(--orange)" }}>send it.</span>
          </h1>
          <p className="reveal reveal-3" style={{ fontSize: 18, color: "var(--ink-soft)", maxWidth: 560, margin: "0 auto 72px", lineHeight: 1.5 }}>
            Same vetted courier network. Same live tracking. Pick the speed that matches your situation.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, marginBottom: 48 }}>
          {[
            {
              name: "Planned",
              price: "$29",
              tag: "Save $5",
              highlight: false,
              headline: "You pick the window. We bring you $5 off for letting us plan ahead.",
              features: [
                "Pick a 2-hour delivery window",
                "Today or up to 30 days out",
                "Guaranteed delivery window",
                "Perfect for planned handoffs",
                "$5 less than Standard",
              ],
              cta: "Book Planned",
            },
            {
              name: "Standard",
              price: "$34",
              tag: "Our default",
              highlight: true,
              headline: "Same-day. Dispatched the moment you book. Arrives in 1-3 hours.",
              features: [
                "Dispatched ASAP after booking",
                "Delivered in 1-3 hours typically",
                "Live SMS tracking",
                "No schedule required",
                "Most popular choice",
              ],
              cta: "Book Standard",
            },
            {
              name: "Rush",
              price: "$49",
              tag: "Under 1 hour",
              highlight: false,
              headline: "For the closing that moved up. The case that has to be there now.",
              features: [
                "Delivered in under 60 minutes",
                "Priority courier dispatch",
                "Confirmed before charge",
                "Card only charged if we commit",
                "For genuine emergencies",
              ],
              cta: "Book Rush",
            },
          ].map((tier) => (
            <div key={tier.name} className="lift" style={{
              background: tier.highlight ? "var(--ink)" : "rgba(255,255,255,0.6)",
              backdropFilter: "blur(10px)",
              color: tier.highlight ? "var(--cream)" : "var(--ink)",
              border: tier.highlight ? "1px solid var(--ink)" : "1px solid var(--line)",
              borderRadius: 20, padding: "36px 32px", position: "relative",
            }}>
              {tier.highlight && (
                <div style={{
                  position: "absolute", top: -1, left: "50%", transform: "translate(-50%, -50%)",
                  background: "var(--orange)", color: "var(--cream)",
                  fontSize: 11, fontWeight: 500, letterSpacing: "-0.01em",
                  padding: "5px 14px", borderRadius: 999,
                }}>Most popular</div>
              )}

              <div style={{ fontSize: 13, color: tier.highlight ? "rgba(250,247,242,0.6)" : "var(--ink-muted)", marginBottom: 6, fontWeight: 500 }}>
                {tier.tag}
              </div>
              <div className="serif" style={{ fontSize: 32, fontWeight: 500, letterSpacing: "-0.02em", marginBottom: 16 }}>
                {tier.name}
              </div>

              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 14 }}>
                <span className="serif" style={{ fontSize: 56, fontWeight: 500, letterSpacing: "-0.03em", lineHeight: 1 }}>{tier.price}</span>
                <span style={{ fontSize: 14, color: tier.highlight ? "rgba(250,247,242,0.5)" : "var(--ink-muted)" }}>/ delivery</span>
              </div>
              <p className="serif" style={{ fontSize: 16, fontStyle: "italic", color: tier.highlight ? "rgba(250,247,242,0.8)" : "var(--ink-soft)", margin: "0 0 28px", lineHeight: 1.4 }}>
                {tier.headline}
              </p>

              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px" }}>
                {tier.features.map(f => (
                  <li key={f} style={{ display: "flex", gap: 10, marginBottom: 12, fontSize: 14, color: tier.highlight ? "rgba(250,247,242,0.85)" : "var(--ink-soft)" }}>
                    <span style={{ color: "var(--orange)", fontWeight: 600, flexShrink: 0 }}>→</span>{f}
                  </li>
                ))}
              </ul>

              <button onClick={() => setActive("Book Now")} style={{
                width: "100%",
                background: tier.highlight ? "var(--cream)" : "var(--ink)",
                color: tier.highlight ? "var(--ink)" : "var(--cream)",
                border: "none", padding: "13px", borderRadius: 999, cursor: "pointer",
                fontFamily: "'Geist', sans-serif", fontSize: 14, fontWeight: 500,
                transition: "transform 0.2s",
              }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >{tier.cta}</button>
            </div>
          ))}
        </div>

        <div style={{
          background: "var(--paper)",
          border: "1px solid var(--line)",
          borderRadius: 16, padding: "28px 32px", marginBottom: 20,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }} className="mobile-scope-grid">
            <div>
              <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontWeight: 500 }}>
                ✓ What we're great for
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.8 }}>
                <li>Documents, contracts, signed papers</li>
                <li>Gift baskets, flowers, scheduled drop-offs</li>
                <li>Printed materials, marketing, proofs</li>
                <li>Office supplies between locations</li>
                <li>Routine business items under $500</li>
              </ul>
            </div>
            <div>
              <div className="mono" style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontWeight: 500 }}>
                ✗ What we don't carry
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.8 }}>
                <li>Items valued over $500</li>
                <li>Cash, checks over $100, jewelry</li>
                <li>Medical specimens, PHI, prescriptions</li>
                <li>Custom or tiered cakes, fragile confections</li>
                <li>Irreplaceable originals, firearms, lab cases</li>
              </ul>
            </div>
          </div>
          <p style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 20, marginBottom: 0, lineHeight: 1.5, fontStyle: "italic" }}>
            Max liability $100 per delivery. For higher-value shipments, a specialty courier with cargo insurance is the right call. See <a href="#" onClick={(e) => { e.preventDefault(); setActive("Terms"); }} style={{ color: "var(--orange)", textDecoration: "underline" }}>Terms</a> for details.
          </p>
        </div>

        <div style={{
          background: "var(--orange-tint)",
          border: "1px solid rgba(242,92,18,0.2)",
          borderRadius: 16, padding: "24px 32px",
          display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
        }}>
          <div className="serif" style={{ fontSize: 32, color: "var(--orange)", fontStyle: "italic", fontWeight: 400, letterSpacing: "-0.02em", flexShrink: 0 }}>
            Rush unavailable?
          </div>
          <div style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.5, flex: 1, minWidth: 300 }}>
            If we can't commit to under 1 hour right now (courier unavailable, traffic, distance), we tell you upfront. <strong style={{ color: "var(--ink)" }}>Your card isn't charged.</strong> Switch to Standard and it still arrives same-day, usually in 1-2 hours.
          </div>
        </div>
      </div>
    </div>
  );
}

function AboutPage({ setActive }) {
  return (
    <div className="mobile-tight-padding-large" style={{ background: "var(--cream)", minHeight: "100vh", padding: "140px 40px 100px", position: "relative", overflow: "hidden" }}>
      <div className="mesh-bg" style={{ opacity: 0.4 }}>
        <div className="mesh-blob mesh-blob-1" />
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div className="mono reveal reveal-1" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 20, fontWeight: 500 }}>
          About LocalRush
        </div>
        <h1 className="serif reveal reveal-2 mobile-hero-title" style={{ fontSize: "clamp(48px, 6.5vw, 84px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.04em", lineHeight: 0.92, margin: "0 0 48px" }}>
          Built for SLC.<br />
          <span style={{ fontStyle: "italic", color: "var(--orange)" }}>Built for business.</span>
        </h1>

        <p className="reveal reveal-3 serif" style={{ fontSize: 26, fontWeight: 400, color: "var(--ink)", lineHeight: 1.35, marginBottom: 32, letterSpacing: "-0.02em" }}>
          Every business in this valley has the same problem: something that needs to get across town, today.
        </p>

        <p style={{ fontSize: 18, color: "var(--ink-soft)", lineHeight: 1.7, marginBottom: 24 }}>
          Sending staff wastes $25–40 in productivity. FedEx and UPS don't do same-day. Driving it yourself costs an hour you don't have.
        </p>
        <p style={{ fontSize: 18, color: "var(--ink-soft)", lineHeight: 1.7, marginBottom: 72 }}>
          LocalRush is the third option. Book online, we dispatch, you track it live. Done by lunch. Under an hour if it's urgent.
        </p>

        <div className="mobile-who-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 24, marginBottom: 72 }}>
          {[
            { title: "Fast", body: "Rush under 1 hour. Standard 1-3. Both same-day." },
            { title: "Tracked", body: "Live GPS sent to you and your recipient automatically." },
            { title: "Professional", body: "Invoicing, support, and account management built in." },
            { title: "Reliable", body: "Vetted courier network with real-time monitoring on every job." },
          ].map(({ title, body }) => (
            <div key={title} style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(10px)", border: "1px solid var(--line)", borderRadius: 14, padding: "24px" }}>
              <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", marginBottom: 8 }}>{title}</div>
              <div style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55 }}>{body}</div>
            </div>
          ))}
        </div>

        <div style={{ background: "var(--ink)", borderRadius: 20, padding: "40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
          <div>
            <div className="serif" style={{ fontSize: 28, fontWeight: 500, color: "var(--cream)", letterSpacing: "-0.02em", marginBottom: 10 }}>
              Questions? <span style={{ fontStyle: "italic", color: "var(--orange)" }}>Just ask.</span>
            </div>
            <a href="mailto:kevin.localrush@gmail.com" style={{ color: "var(--cream)", fontSize: 16, textDecoration: "none" }} className="ul-link">
              kevin.localrush@gmail.com
            </a>
            <div style={{ color: "rgba(250,247,242,0.5)", fontSize: 13, marginTop: 6 }}>
              Open daily · 8am-7pm MST
            </div>
          </div>
          <button onClick={() => setActive("Book Now")} style={{
            background: "var(--orange)", border: "none", color: "var(--cream)",
            padding: "14px 28px", borderRadius: 999, cursor: "pointer",
            fontFamily: "'Geist', sans-serif", fontSize: 15, fontWeight: 500,
            display: "inline-flex", alignItems: "center", gap: 10,
          }}>
            Book a delivery
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ORDER FORM ───────────────────────────────────────────────────────────────
const steps = ["Pickup", "Dropoff", "Details", "Payment"];

const formInputStyle = {
  width: "100%", background: "var(--cream)",
  border: "1px solid var(--line)", borderRadius: 10,
  padding: "13px 16px", color: "var(--ink)", fontSize: 15,
  fontFamily: "'Geist', sans-serif", outline: "none", boxSizing: "border-box",
  transition: "border-color 0.2s",
};
const formLabelStyle = {
  display: "block", fontSize: 11, fontWeight: 500,
  letterSpacing: "0.08em", textTransform: "uppercase",
  color: "var(--ink-muted)", marginBottom: 7,
  fontFamily: "'JetBrains Mono', monospace",
};

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={formLabelStyle}>{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} style={formInputStyle}
        onFocus={(e) => e.target.style.borderColor = "var(--orange)"}
        onBlur={(e) => e.target.style.borderColor = "var(--line)"}
      />
    </div>
  );
}

function getPresetWindows() {
  const windows = [];
  const now = new Date();

  const slots = [
    { startHour: 9, endHour: 11, label: "9am – 11am" },
    { startHour: 11, endHour: 13, label: "11am – 1pm" },
    { startHour: 13, endHour: 15, label: "1pm – 3pm" },
    { startHour: 15, endHour: 17, label: "3pm – 5pm" },
    { startHour: 17, endHour: 19, label: "5pm – 7pm" },
  ];

  slots.forEach(slot => {
    const start = new Date(now);
    start.setHours(slot.startHour, 0, 0, 0);
    const end = new Date(now);
    end.setHours(slot.endHour, 0, 0, 0);

    if (start.getTime() > now.getTime() + 30 * 60 * 1000) {
      windows.push({
        label: `Today · ${slot.label}`,
        pickupStart: start.toISOString(),
        dropoffEnd: end.toISOString(),
      });
    }
  });

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowDay = tomorrow.getDay();
  if (tomorrowDay !== 0) {
    slots.forEach(slot => {
      const start = new Date(tomorrow);
      start.setHours(slot.startHour, 0, 0, 0);
      const end = new Date(tomorrow);
      end.setHours(slot.endHour, 0, 0, 0);
      windows.push({
        label: `Tomorrow · ${slot.label}`,
        pickupStart: start.toISOString(),
        dropoffEnd: end.toISOString(),
      });
    });
  }

  return windows;
}

function OrderFormInner() {
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("preset");
  const [selectedWindow, setSelectedWindow] = useState("");
  const [customPickup, setCustomPickup] = useState("");
  const [customDropoff, setCustomDropoff] = useState("");

  // Geographic validation state
  const [outOfArea, setOutOfArea] = useState(null); // { field, city } when address is outside SLC metro
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

  const [form, setForm] = useState({
    pickupName: "", pickupAddress: "", pickupPhone: "",
    dropoffName: "", dropoffAddress: "", dropoffPhone: "",
    packageType: "Documents",
    speed: "Standard (2-4 hrs)",
    notes: "", email: "",
  });

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const PRICES = {
    "Planned": "$29",
    "Standard (2-4 hrs)": "$34",
    "Rush (Under 1 hr)": "$49",
  };

  // ═══ GEOGRAPHIC SERVICE AREA ═══════════════════════════════════════════
  // LocalRush serves the Wasatch Front: Salt Lake County, Utah County,
  // Davis County, Weber County, and Park City.
  const SLC_METRO_ZIPS = new Set([
    // ─── Salt Lake County ────────────────────────────────────────────────
    // Salt Lake City core
    "84101","84102","84103","84104","84105","84106","84108","84109","84111","84112","84113","84115","84116","84117","84119","84121","84124",
    // South Salt Lake, Murray, Holladay, Millcreek
    "84107","84123","84118",
    // West Valley, Kearns, Taylorsville
    "84120","84128","84129",
    // Midvale, Sandy, Draper, Cottonwood Heights
    "84047","84070","84090","84092","84093","84094","84020",
    // Bluffdale, Herriman, Riverton, West Jordan, South Jordan, Magna
    "84065","84096","84088","84044","84081","84084","84095","84009",

    // ─── Utah County ─────────────────────────────────────────────────────
    // Provo
    "84601","84602","84603","84604","84605","84606",
    // Orem
    "84057","84058","84059","84097",
    // Lehi, American Fork, Pleasant Grove, Lindon
    "84043","84003","84004","84005","84062","84042",
    // Saratoga Springs, Eagle Mountain, Cedar Hills
    "84045","84005",
    // Spanish Fork, Springville, Payson, Mapleton
    "84660","84663","84651","84664",
    // Alpine, Highland
    "84004","84003",

    // ─── Davis County ────────────────────────────────────────────────────
    // Bountiful, Centerville, Farmington, Kaysville
    "84010","84014","84025","84037",
    // Layton, Clearfield, Syracuse, Clinton
    "84040","84041","84015","84075",
    // North Salt Lake, Woods Cross
    "84054","84087",

    // ─── Weber County ────────────────────────────────────────────────────
    // Ogden, South Ogden, North Ogden, Roy, Riverdale, Washington Terrace
    "84401","84403","84404","84405","84414","84415","84067",

    // ─── Park City / Summit ──────────────────────────────────────────────
    "84060","84068","84098",
  ]);

  // Cities we explicitly recognize as served (appearing in address)
  const SLC_METRO_CITIES = [
    // Salt Lake County
    "salt lake city","salt lake","slc",
    "south salt lake","murray","holladay","millcreek","mill creek",
    "west valley city","west valley","kearns","taylorsville",
    "midvale","sandy","draper","cottonwood heights","cottonwood",
    "bluffdale","herriman","riverton","west jordan","south jordan","magna",
    // Utah County
    "provo","orem","lehi","american fork","pleasant grove","lindon",
    "saratoga springs","eagle mountain","cedar hills","alpine","highland",
    "spanish fork","springville","payson","mapleton",
    // Davis County
    "bountiful","centerville","farmington","kaysville",
    "layton","clearfield","syracuse","clinton",
    "north salt lake","woods cross",
    // Weber County
    "ogden","south ogden","north ogden","roy","riverdale","washington terrace",
    // Summit County
    "park city","kamas","heber","heber city",
  ];

  // Cities we explicitly DO NOT serve (outside reasonable courier range)
  const OUT_OF_AREA_CITIES = {
    "st george": "St. George",
    "saint george": "St. George",
    "logan": "Logan",
    "tooele": "Tooele",
    "cedar city": "Cedar City",
    "moab": "Moab",
    "vernal": "Vernal",
    "price": "Price",
  };

  function validateAddress(address) {
    if (!address || address.trim().length < 5) {
      return { valid: null, reason: "empty" }; // empty/short = don't validate yet
    }
    const lower = address.toLowerCase();

    // Check for explicitly out-of-area city names
    for (const [key, displayName] of Object.entries(OUT_OF_AREA_CITIES)) {
      // use word boundary match to avoid false positives (e.g., "loganville" shouldn't hit "logan")
      const re = new RegExp(`\\b${key}\\b`, "i");
      if (re.test(lower)) {
        return { valid: false, city: displayName };
      }
    }

    // Check for served city names
    for (const city of SLC_METRO_CITIES) {
      const re = new RegExp(`\\b${city}\\b`, "i");
      if (re.test(lower)) {
        return { valid: true };
      }
    }

    // Check ZIP code
    const zipMatch = address.match(/\b(\d{5})(?:-\d{4})?\b/);
    if (zipMatch) {
      const zip = zipMatch[1];
      if (SLC_METRO_ZIPS.has(zip)) return { valid: true };
      // Known Utah zips outside our area
      if (zip.startsWith("84")) return { valid: false, city: "that Utah location" };
      return { valid: false, city: "that location" };
    }

    // No city or zip matched — probably incomplete. Let them continue; we'll catch it
    // again when Uber Direct quote returns "no courier available."
    return { valid: null, reason: "indeterminate" };
  }

  function handleContinue() {
    // On step 0, validate pickup address
    if (step === 0) {
      const result = validateAddress(form.pickupAddress);
      if (result.valid === false) {
        setOutOfArea({ field: "pickup", city: result.city });
        return;
      }
    }
    // On step 1, validate dropoff address
    if (step === 1) {
      const result = validateAddress(form.dropoffAddress);
      if (result.valid === false) {
        setOutOfArea({ field: "dropoff", city: result.city });
        return;
      }
    }
    // Passed validation — advance or submit
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  }

  async function submitWaitlist() {
    if (!waitlistEmail) return;
    // For now, capture via mailto — user's email client handles the delivery.
    // Future: POST to /api/waitlist so Kevin gets a real lead.
    const subject = encodeURIComponent(`LocalRush waitlist: ${outOfArea?.city || "new city"}`);
    const body = encodeURIComponent(
      `Requesting service in: ${outOfArea?.city || "(city)"}\nEmail: ${waitlistEmail}\n\nSent from localrush.io booking form.`
    );
    window.open(`mailto:kevin.localrush@gmail.com?subject=${subject}&body=${body}`);
    setWaitlistSubmitted(true);
  }


  const presetWindows = getPresetWindows();

  function getScheduledTimestamps() {
    if (form.speed !== "Planned") return { scheduledPickupStart: null, scheduledDropoffEnd: null };

    if (scheduleMode === "preset" && selectedWindow) {
      const win = presetWindows.find(w => w.label === selectedWindow);
      if (!win) return { scheduledPickupStart: null, scheduledDropoffEnd: null };
      return { scheduledPickupStart: win.pickupStart, scheduledDropoffEnd: win.dropoffEnd };
    }

    if (scheduleMode === "custom" && customPickup && customDropoff) {
      return {
        scheduledPickupStart: new Date(customPickup).toISOString(),
        scheduledDropoffEnd: new Date(customDropoff).toISOString(),
      };
    }

    return { scheduledPickupStart: null, scheduledDropoffEnd: null };
  }

  function switchToStandard() {
    setForm(prev => ({ ...prev, speed: "Standard (2-4 hrs)" }));
    setSubmitted(false);
    setResult(null);
    setCardError(null);
  }

  async function handleSubmit() {
    if (!stripe || !elements) {
      setCardError("Payment system still loading. Please wait a moment.");
      return;
    }

    if (form.speed === "Planned") {
      const { scheduledPickupStart, scheduledDropoffEnd } = getScheduledTimestamps();
      if (!scheduledPickupStart || !scheduledDropoffEnd) {
        setCardError("Please select a delivery window for your Planned delivery.");
        return;
      }
    }

    setLoading(true);
    setCardError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card", card: cardElement,
        billing_details: { name: form.pickupName, email: form.email, phone: form.pickupPhone },
      });
      if (pmError) {
        setCardError(pmError.message);
        setLoading(false);
        return;
      }

      const schedule = getScheduledTimestamps();

      const res = await fetch(`${BACKEND}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          ...schedule,
          planType: "paygo",
          paymentMethodId: paymentMethod.id,
        }),
      });
      const data = await res.json();

      if (data.requiresAction && data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmError) {
          setResult({ error: confirmError.message });
          setSubmitted(true);
          setLoading(false);
          return;
        }
        setResult({ error: "Card verified. Please resubmit your order." });
        setSubmitted(true);
        setLoading(false);
        return;
      }

      if (!res.ok) {
        setResult({
          error: data.message || data.error || "Booking failed",
          code: data.code,
        });
        setSubmitted(true);
        setLoading(false);
        return;
      }

      setResult(data);
      setSubmitted(true);
    } catch (err) {
      setResult({ error: "Something went wrong. Please try again or email kevin.localrush@gmail.com" });
      setSubmitted(true);
    }
    setLoading(false);
  }

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "32px 0" }}>
        {result?.success ? (
          <>
            {/* Soft orange checkmark badge, not a raw character */}
            <div style={{
              width: 72, height: 72, borderRadius: "50%",
              background: "var(--orange-tint)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 28,
              border: "1px solid rgba(242,92,18,0.2)",
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M5 12.5l5 5 9-11" stroke="var(--orange)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Hero confirmation — serif, giant, branded */}
            <h2 className="serif" style={{
              fontSize: "clamp(44px, 7vw, 68px)",
              fontWeight: 400,
              color: "var(--ink)",
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
              margin: "0 0 8px",
            }}>
              Consider it<br/>
              <span style={{ fontStyle: "italic", color: "var(--orange)" }}>done.</span>
            </h2>

            {/* Tagline under hero */}
            <p className="serif" style={{
              fontSize: 17,
              fontStyle: "italic",
              color: "var(--ink-muted)",
              margin: "16px 0 32px",
              lineHeight: 1.4,
            }}>
              Your delivery is on its way.
            </p>

            {/* Order details card */}
            <div style={{
              background: "rgba(255,255,255,0.6)",
              border: "1px solid var(--line)",
              borderRadius: 14,
              padding: "18px 20px",
              marginBottom: 24,
              textAlign: "left",
            }}>
              <div className="mono" style={{ fontSize: 10, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                Order number
              </div>
              <div className="mono" style={{ fontSize: 16, color: "var(--ink)", fontWeight: 500, marginBottom: 14 }}>
                {result.orderId}
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.55 }}>
                Tracking link sent to your phone. Questions? Email{" "}
                <a href="mailto:kevin.localrush@gmail.com" style={{ color: "var(--orange)", textDecoration: "none" }}>
                  kevin.localrush@gmail.com
                </a>
              </div>
            </div>

            <button onClick={() => {
              setSubmitted(false); setStep(0); setResult(null); setCardError(null); setAgreed(false);
              setSelectedWindow(""); setCustomPickup(""); setCustomDropoff("");
              setForm({ pickupName: "", pickupAddress: "", pickupPhone: "", dropoffName: "", dropoffAddress: "", dropoffPhone: "", packageType: "Documents", speed: "Standard (2-4 hrs)", notes: "", email: "" });
            }} className="btn-secondary">
              Book another
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 48, marginBottom: 20, color: "var(--orange)" }}>!</div>

            {result?.code === "rush_unavailable" ? (
              <>
                <h2 className="serif" style={{ fontSize: 30, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 16px" }}>
                  Rush unavailable <span style={{ fontStyle: "italic", color: "var(--ink-muted)" }}>right now.</span>
                </h2>
                <p style={{ color: "var(--ink-soft)", fontSize: 15, marginBottom: 8, lineHeight: 1.6, textAlign: "left", background: "var(--cream-2)", padding: "16px 20px", borderRadius: 10 }}>
                  We couldn't commit to under 1 hour for this pickup location right now — no courier can make it in that window.
                  <br /><br />
                  <strong style={{ color: "var(--ink)" }}>Your card wasn't charged.</strong>
                </p>
                <p style={{ color: "var(--ink-muted)", fontSize: 14, marginBottom: 24, marginTop: 20 }}>
                  Switch to <strong style={{ color: "var(--ink)" }}>Standard ($34)</strong> — usually arrives in 1-2 hours. Still same-day.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={switchToStandard} className="btn-primary">
                    Switch to Standard ($34)
                  </button>
                  <button onClick={() => { setSubmitted(false); setResult(null); }} className="btn-secondary">
                    Go back
                  </button>
                </div>
              </>
            ) : result?.code === "planned_unavailable" ? (
              <>
                <h2 className="serif" style={{ fontSize: 30, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 16px" }}>
                  Window <span style={{ fontStyle: "italic", color: "var(--ink-muted)" }}>unavailable.</span>
                </h2>
                <p style={{ color: "var(--ink-soft)", fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>
                  {result.error || "We couldn't secure a courier for that window."}
                  <br />Your card wasn't charged.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={switchToStandard} className="btn-primary">
                    Switch to Standard ($34)
                  </button>
                  <button onClick={() => { setSubmitted(false); setResult(null); }} className="btn-secondary">
                    Try different window
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="serif" style={{ fontSize: 30, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 12px" }}>
                  Booking issue
                </h2>
                <p style={{ color: "var(--ink-soft)", fontSize: 15, marginBottom: 8, lineHeight: 1.5 }}>
                  {result?.error || "We couldn't complete your booking automatically."}
                </p>
                <p style={{ color: "var(--ink-muted)", fontSize: 14, marginBottom: 28 }}>
                  If your card was charged, it's been refunded.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <a href="mailto:kevin.localrush@gmail.com" className="btn-primary" style={{ textDecoration: "none" }}>Email us</a>
                  <button onClick={() => { setSubmitted(false); setResult(null); setCardError(null); }} className="btn-secondary">
                    Try again
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  const cardElementOptions = {
    style: {
      base: {
        color: "var(--ink)",
        fontFamily: "'Geist', sans-serif",
        fontSize: "15px",
        "::placeholder": { color: "var(--ink-faint)" },
      },
      invalid: { color: "#C4341B" },
    },
  };

  const stepContent = [
    <>
      <Field label="Business or contact name" value={form.pickupName} onChange={set("pickupName")} placeholder="Apex Dental Group" />
      <Field label="Pickup address" value={form.pickupAddress} onChange={set("pickupAddress")} placeholder="123 Main St, Salt Lake City, UT" />
      <Field label="Pickup phone" value={form.pickupPhone} onChange={set("pickupPhone")} type="tel" placeholder="(801) 555-0100" />
      <Field label="Your email" value={form.email} onChange={set("email")} type="email" placeholder="office@apexdental.com" />
    </>,
    <>
      <Field label="Recipient name" value={form.dropoffName} onChange={set("dropoffName")} placeholder="Valley Imaging Center" />
      <Field label="Dropoff address" value={form.dropoffAddress} onChange={set("dropoffAddress")} placeholder="456 Oak Ave, Murray, UT" />
      <Field label="Recipient phone" value={form.dropoffPhone} onChange={set("dropoffPhone")} type="tel" placeholder="(801) 555-0200" />
    </>,
    <>
      <div style={{ marginBottom: 18 }}>
        <label style={formLabelStyle}>Package type</label>
        <select value={form.packageType} onChange={set("packageType")} style={{ ...formInputStyle, appearance: "none", cursor: "pointer" }}>
          {["Documents", "Small Package", "Medical Supplies", "Equipment Sample", "Keys / Access Cards", "Other"].map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={formLabelStyle}>Delivery speed</label>
        <select value={form.speed} onChange={(e) => {
          set("speed")(e);
          setSelectedWindow(""); setCustomPickup(""); setCustomDropoff("");
        }} style={{ ...formInputStyle, appearance: "none", cursor: "pointer" }}>
          <option value="Planned">Planned — $29 (pick your window, save $5)</option>
          <option value="Standard (2-4 hrs)">Standard — $34 (same-day, 1-3 hrs)</option>
          <option value="Rush (Under 1 hr)">Rush — $49 (under 1 hour)</option>
        </select>
      </div>

      {form.speed === "Planned" && (
        <div style={{
          marginBottom: 18, padding: "16px 18px",
          background: "var(--orange-tint)",
          border: "1px solid rgba(242,92,18,0.2)",
          borderRadius: 10,
        }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button type="button" onClick={() => setScheduleMode("preset")} style={{
              flex: 1, padding: "8px 12px", fontSize: 13,
              background: scheduleMode === "preset" ? "var(--ink)" : "transparent",
              color: scheduleMode === "preset" ? "var(--cream)" : "var(--ink-soft)",
              border: "1px solid " + (scheduleMode === "preset" ? "var(--ink)" : "var(--line)"),
              borderRadius: 999, cursor: "pointer", fontFamily: "'Geist', sans-serif", fontWeight: 500,
            }}>Pre-set windows</button>
            <button type="button" onClick={() => setScheduleMode("custom")} style={{
              flex: 1, padding: "8px 12px", fontSize: 13,
              background: scheduleMode === "custom" ? "var(--ink)" : "transparent",
              color: scheduleMode === "custom" ? "var(--cream)" : "var(--ink-soft)",
              border: "1px solid " + (scheduleMode === "custom" ? "var(--ink)" : "var(--line)"),
              borderRadius: 999, cursor: "pointer", fontFamily: "'Geist', sans-serif", fontWeight: 500,
            }}>Custom time</button>
          </div>

          {scheduleMode === "preset" && (
            <div>
              <label style={formLabelStyle}>Pick a delivery window</label>
              <select value={selectedWindow} onChange={(e) => setSelectedWindow(e.target.value)} style={{ ...formInputStyle, background: "white", appearance: "none", cursor: "pointer" }}>
                <option value="">— Select a window —</option>
                {presetWindows.map(w => (
                  <option key={w.label} value={w.label}>{w.label}</option>
                ))}
              </select>
              {presetWindows.length === 0 && (
                <div style={{ fontSize: 13, color: "var(--ink-muted)", marginTop: 8 }}>
                  No windows available today. Switch to Custom to pick a later time, or select Standard / Rush.
                </div>
              )}
            </div>
          )}

          {scheduleMode === "custom" && (
            <div>
              <label style={formLabelStyle}>Pickup ready time</label>
              <input type="datetime-local" value={customPickup} onChange={(e) => setCustomPickup(e.target.value)} style={{ ...formInputStyle, background: "white", marginBottom: 12 }} />
              <label style={formLabelStyle}>Dropoff deadline</label>
              <input type="datetime-local" value={customDropoff} onChange={(e) => setCustomDropoff(e.target.value)} style={{ ...formInputStyle, background: "white" }} />
              <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 8, lineHeight: 1.4 }}>
                Pickup times available 8am – 7pm, 7 days a week. Dropoffs complete by 8:30pm latest. Dropoff deadline must be at least 1 hour after pickup time.
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <label style={formLabelStyle}>Special instructions</label>
        <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Leave at front desk, fragile items..." style={{ ...formInputStyle, resize: "vertical", lineHeight: 1.5 }} />
      </div>
    </>,
    <div>
      <div style={{ marginBottom: 20, padding: "14px 16px", background: "var(--cream-2)", borderRadius: 10 }}>
        {[
          ["From", form.pickupName],
          ["To", form.dropoffName],
          ["Speed", form.speed],
          ...(form.speed === "Planned" ? [["Window",
            scheduleMode === "preset" ? (selectedWindow || "Not set") :
            (customPickup && customDropoff ? `${new Date(customPickup).toLocaleString()} → ${new Date(customDropoff).toLocaleString()}` : "Not set")
          ]] : []),
        ].map(([label, val]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, gap: 12 }}>
            <span style={{ color: "var(--ink-muted)", flexShrink: 0 }}>{label}</span>
            <span style={{ color: "var(--ink)", fontWeight: 500, textAlign: "right" }}>{val || "—"}</span>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16, background: "var(--ink)", color: "var(--cream)", borderRadius: 10, padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="mono" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Total</span>
        <span className="serif" style={{ fontSize: 28, fontWeight: 500, letterSpacing: "-0.02em" }}>{PRICES[form.speed]}</span>
      </div>

      {form.speed === "Rush (Under 1 hr)" && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "var(--orange-tint)", border: "1px solid rgba(242,92,18,0.2)", borderRadius: 10, fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.5 }}>
          <strong style={{ color: "var(--orange-deep)" }}>Rush guarantee:</strong> If no courier can hit under 1 hour right now, we'll tell you upfront and your card won't be charged.
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={formLabelStyle}>Card details</label>
        <div style={{
          background: "var(--cream)",
          border: `1px solid ${cardError ? "#C4341B" : "var(--line)"}`,
          borderRadius: 10, padding: "14px 16px",
        }}>
          <CardElement
            options={cardElementOptions}
            onChange={(e) => { setCardError(e.error ? e.error.message : null); setCardComplete(e.complete); }}
          />
        </div>
        {cardError && (
          <div style={{ color: "#C4341B", fontSize: 12, marginTop: 6 }}>{cardError}</div>
        )}
      </div>

      <div style={{ marginTop: 16, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <input type="checkbox" id="tos" checked={agreed} onChange={e => setAgreed(e.target.checked)}
          style={{ marginTop: 3, accentColor: "var(--orange)", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }} />
        <label htmlFor="tos" style={{ color: "var(--ink-muted)", fontSize: 12, lineHeight: 1.5, cursor: "pointer" }}>
          I confirm my shipment is valued at <strong style={{ color: "var(--ink-soft)" }}>$500 or less</strong> and does not contain prohibited items (cash over $100, jewelry, controlled substances, medical specimens, irreplaceable originals, or items containing PHI). I agree to the <a href="https://localrush.io/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--orange)", textDecoration: "underline" }}>Terms of Service</a> (max liability $100) and <a href="https://localrush.io/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--orange)", textDecoration: "underline" }}>Privacy Policy</a>.
        </label>
      </div>
    </div>,
  ];

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}>

      {/* ═══ OUT-OF-AREA MODAL ═══════════════════════════════════════════ */}
      {outOfArea && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 15, 16, 0.55)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
          zIndex: 1000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setOutOfArea(null); }}
        >
          <div style={{
            background: "var(--cream)",
            borderRadius: 20,
            padding: "36px 32px",
            maxWidth: 440,
            width: "100%",
            boxShadow: "0 30px 80px -20px rgba(15,15,16,0.4)",
            border: "1px solid var(--line)",
          }}>
            {!waitlistSubmitted ? (
              <>
                <div className="mono" style={{ fontSize: 10, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14, fontWeight: 500 }}>
                  Service area
                </div>
                <h3 className="serif" style={{ fontSize: 32, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.03em", lineHeight: 1.05, margin: "0 0 16px" }}>
                  We don't serve <span style={{ fontStyle: "italic", color: "var(--orange)" }}>{outOfArea.city}</span> yet.
                </h3>
                <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55, margin: "0 0 24px" }}>
                  LocalRush serves the Wasatch Front — Salt Lake, Utah, Davis, and Weber counties, plus Park City. For {outOfArea.city}, drop your email and we'll let you know the moment we launch there.
                </p>

                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="you@business.com"
                  style={{
                    width: "100%", padding: "12px 14px",
                    border: "1px solid var(--line)", borderRadius: 10,
                    fontSize: 15, fontFamily: "'Geist', sans-serif",
                    background: "white",
                    marginBottom: 14,
                    boxSizing: "border-box",
                  }}
                />

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => { setOutOfArea(null); setWaitlistEmail(""); }}
                    className="btn-secondary"
                    style={{ flex: 1, padding: "12px", fontSize: 14 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitWaitlist}
                    disabled={!waitlistEmail.includes("@")}
                    className="btn-primary"
                    style={{ flex: 2, padding: "12px", fontSize: 14, justifyContent: "center" }}
                  >
                    Notify me
                  </button>
                </div>

                <p style={{ color: "var(--ink-faint)", fontSize: 11, margin: "16px 0 0", textAlign: "center" }}>
                  Or update the address above if you meant a different location.
                </p>
              </>
            ) : (
              <>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "var(--orange-tint)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 20,
                  border: "1px solid rgba(242,92,18,0.2)",
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12.5l5 5 9-11" stroke="var(--orange)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="serif" style={{ fontSize: 28, fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 12px" }}>
                  You're on the list.
                </h3>
                <p style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.55, margin: "0 0 24px" }}>
                  We'll email you the moment LocalRush launches in {outOfArea.city}. In the meantime, if you have questions, reach Kevin directly at <a href="mailto:kevin.localrush@gmail.com" style={{ color: "var(--orange)", textDecoration: "none" }}>kevin.localrush@gmail.com</a>.
                </p>
                <button
                  onClick={() => { setOutOfArea(null); setWaitlistEmail(""); setWaitlistSubmitted(false); }}
                  className="btn-primary"
                  style={{ width: "100%", padding: "12px", fontSize: 14, justifyContent: "center" }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 32 }}>
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1 }}>
            <div style={{ height: 2, borderRadius: 2, background: i <= step ? "var(--orange)" : "var(--line)", marginBottom: 6, transition: "background 0.3s" }} />
            <div className="mono" style={{ fontSize: 10, color: i === step ? "var(--orange)" : "var(--ink-faint)", fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>{s}</div>
          </div>
        ))}
      </div>

      <h3 className="serif" style={{ fontSize: 28, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", margin: "0 0 24px" }}>
        {["Pickup info", "Dropoff info", "Package details", "Payment"][step]}
      </h3>

      {stepContent[step]}

      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="btn-secondary" style={{ flex: 1, padding: "13px" }}>Back</button>
        )}
        <button
          onClick={handleContinue}
          disabled={loading || (step === steps.length - 1 && (!agreed || !cardComplete))}
          className="btn-primary"
          style={{ flex: 2, padding: "13px", justifyContent: "center" }}
        >
          {loading ? "Processing..." : step === steps.length - 1 ? `Pay ${PRICES[form.speed]} & book` : "Continue"}
          {!loading && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </button>
      </div>
    </div>
  );
}

function OrderForm() {
  const [stripePromise, setStripePromise] = useState(null);
  useEffect(() => { setStripePromise(getStripePromise()); }, []);

  if (!stripePromise) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ink-muted)" }}>
        Loading payment form...
      </div>
    );
  }
  return <Elements stripe={stripePromise}><OrderFormInner /></Elements>;
}

function BookPage() {
  return (
    <div className="mobile-tight-padding-large" style={{ background: "var(--cream)", minHeight: "100vh", padding: "140px 40px 80px", position: "relative", overflow: "hidden" }}>
      <div className="mesh-bg" style={{ opacity: 0.5 }}>
        <div className="mesh-blob mesh-blob-2" />
        <div className="mesh-blob mesh-blob-3" />
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <Logo size={36} textSize={26} />
          <p className="serif" style={{ color: "var(--ink-muted)", fontSize: 16, fontStyle: "italic", margin: "16px 0 0", fontWeight: 400 }}>
            Same-day B2B delivery · Salt Lake City
          </p>
        </div>
        <div className="mobile-book-card" style={{
          background: "rgba(255,255,255,0.8)", backdropFilter: "blur(20px)",
          border: "1px solid var(--line)", borderRadius: 20,
          padding: "36px 32px",
          boxShadow: "0 20px 60px -30px rgba(15,15,16,0.15)",
        }}>
          <OrderForm />
        </div>
        <p style={{ textAlign: "center", color: "var(--ink-faint)", fontSize: 12, marginTop: 20 }}>
          Secure payment by Stripe · localrush.io
        </p>
      </div>
    </div>
  );
}

function TermsPage({ setActive }) {
  const sections = [
    { n: "01", title: "Overview", body: "LocalRush is a same-day delivery coordination service operating in the Salt Lake City, Utah metropolitan area. We connect businesses with third-party delivery drivers through a professional third-party driver network. LocalRush is a broker and coordinator. We do not employ drivers, own vehicles, or physically handle any packages. All physical delivery services are performed by independent third-party carriers." },
    { n: "02", title: "Services Provided", body: "LocalRush provides same-day local delivery coordination within the Salt Lake City metro area, automated driver dispatch via third-party delivery networks, real-time SMS tracking notifications, digital invoicing and payment processing, and customer support via email during business hours. We reserve the right to refuse any delivery request at our sole discretion." },
    { n: "03", title: "Broker and Intermediary Status", body: "You expressly acknowledge that LocalRush acts solely as an intermediary between you and third-party delivery drivers. LocalRush does not employ, supervise, or control the drivers who fulfill deliveries. The contractual relationship for physical delivery exists between you and the third-party carrier network." },
    { n: "04", title: "Delivery Windows", body: "All delivery time estimates are estimates only and are not guarantees, except for Planned and Rush tiers which are enforced by our dispatch partner. Factors outside our control including driver availability, traffic, weather, and incorrect address information may affect timing. LocalRush is not liable for any loss, damage, or consequential harm resulting from a delivery that does not arrive within the estimated window." },
    { n: "05", title: "Prohibited Items", body: "LocalRush is designed for routine business deliveries. The following items may not be shipped through LocalRush under any circumstances: items with a declared value exceeding $500; cash, checks over $100, or other negotiable instruments; jewelry, watches, precious metals, or gemstones; illegal or controlled substances, including prescription medications; firearms, ammunition, or weapons; hazardous materials or flammable liquids; live animals; perishable food or medical specimens requiring temperature control or cold-chain handling; original legal documents that are irreplaceable or have legal standing (wet-signed wills, original notarized deeds, court-filed originals); items containing Protected Health Information (PHI) subject to HIPAA; high-value dental lab cases, appliances, or prosthetics; items over 50 lbs; or any item illegal to transport under federal, state, or local law." },
    { n: "06", title: "Package Value, Loss, and Damage", body: "LOCALRUSH IS A COURIER FOR LOW-VALUE, ROUTINE BUSINESS SHIPMENTS. By booking a delivery, you represent and warrant that the contents of your package have a declared value of $500 or less. LocalRush's maximum liability for any lost, damaged, or undelivered package is limited to $100 per shipment, regardless of actual contents value. For packages delayed but ultimately completed: no refund. For packages where customer provided a wrong address: no refund. Customer assumes all risk for any value in excess of the $100 liability limit and is solely responsible for insuring higher-value items independently. Claims must be filed within 24 hours of the scheduled delivery time." },
    { n: "07", title: "Limitation of Liability", body: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, LOCALRUSH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOST PROFITS, MISSED DEADLINES, LOSS OF CLIENTS, MISSED COURT FILINGS, MISSED CLOSINGS, LOST BUSINESS OPPORTUNITIES, OR ANY DAMAGES ARISING FROM THE ACTS OR OMISSIONS OF THIRD-PARTY DRIVERS. IN NO EVENT SHALL LOCALRUSH'S TOTAL LIABILITY FOR ANY SINGLE DELIVERY EXCEED $100 OR THE AMOUNT YOU PAID FOR THE SPECIFIC DELIVERY, WHICHEVER IS GREATER. LOCALRUSH DOES NOT GUARANTEE DELIVERY BY ANY SPECIFIC TIME AND IS NOT SUITABLE FOR TIME-CRITICAL SHIPMENTS WHERE DELAY WOULD CAUSE SIGNIFICANT HARM." },
    { n: "08", title: "Payment Terms", body: "All deliveries are charged at the time of booking. For Rush and Planned deliveries, if our dispatch partner cannot commit to the requested time window, the booking is rejected before any charge is made to your card." },
    { n: "09", title: "Cancellations", body: "You may cancel at no charge if the driver has not yet been assigned. Once a driver is assigned and en route, cancellations are subject to a $10 fee. No refunds once a driver has picked up the package. LocalRush may cancel any delivery at any time due to driver unavailability, unsafe conditions, or prohibited items — in such cases a full refund is issued." },
    { n: "10", title: "Customer Responsibilities", body: "You are solely responsible for providing accurate pickup and dropoff information, ensuring someone is available at pickup, ensuring the package is properly packaged, ensuring the recipient is available at dropoff, and ensuring contents comply with our prohibited items policy." },
    { n: "11", title: "Privacy and Data", body: "LocalRush collects your name, business name, phone number, email, and delivery address solely for fulfilling your delivery request, sending tracking notifications, and processing payment. We do not sell your information to third parties." },
    { n: "12", title: "Governing Law", body: "These Terms are governed by the laws of the State of Utah. Any dispute shall be resolved through binding arbitration in Salt Lake County, Utah. YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT AGAINST LOCALRUSH." },
  ];

  return (
    <div className="mobile-tight-padding-large" style={{ background: "var(--cream)", minHeight: "100vh", padding: "140px 40px 100px" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 20, fontWeight: 500 }}>
          Legal
        </div>
        <h1 className="serif mobile-hero-title" style={{ fontSize: "clamp(48px, 6vw, 72px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.04em", lineHeight: 0.95, margin: "0 0 16px" }}>
          Terms of service
        </h1>
        <p className="mono" style={{ color: "var(--ink-faint)", fontSize: 12, marginBottom: 56, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Effective April 21, 2026 · LocalRush · Salt Lake City, UT
        </p>

        <div style={{ background: "var(--orange-tint)", border: "1px solid rgba(242,92,18,0.2)", borderRadius: 12, padding: "18px 22px", marginBottom: 56 }}>
          <p style={{ color: "var(--ink-soft)", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: "var(--orange-deep)" }}>Please read sections 05, 06, and 07 carefully.</strong> LocalRush is a courier for routine, low-value business shipments. Maximum package value is $500. Maximum LocalRush liability per delivery is $100. By submitting an order, you confirm you've read, understood, and agreed to these Terms in full.
          </p>
        </div>

        {sections.map(({ n, title, body }) => (
          <div key={n} style={{ marginBottom: 40, paddingBottom: 40, borderBottom: "1px solid var(--line)" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 14 }}>
              <span className="mono" style={{ fontSize: 13, color: "var(--orange)", fontWeight: 500 }}>{n}</span>
              <h2 className="serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", margin: 0 }}>{title}</h2>
            </div>
            <p style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.7, margin: 0 }}>{body}</p>
          </div>
        ))}

        <div style={{ background: "var(--ink)", borderRadius: 14, padding: "28px", marginTop: 16 }}>
          <div className="serif" style={{ fontSize: 20, fontWeight: 500, color: "var(--cream)", letterSpacing: "-0.02em", marginBottom: 10 }}>
            Contact
          </div>
          <a href="mailto:kevin.localrush@gmail.com" style={{ color: "var(--orange)", fontSize: 16, textDecoration: "none" }} className="ul-link">kevin.localrush@gmail.com</a>
          <div style={{ color: "rgba(250,247,242,0.5)", fontSize: 13, marginTop: 4 }}>Open daily · 8am-7pm MST</div>
        </div>
      </div>
    </div>
  );
}

function PrivacyPage({ setActive }) {
  const sections = [
    { n: "01", title: "Who We Are", body: "LocalRush is a same-day delivery coordination service based in Salt Lake City, Utah, operated by Kevin Court as a sole proprietorship. This Privacy Policy explains what information we collect, how we use it, who we share it with, and the rights you have regarding your data. We take your privacy seriously and only collect information necessary to fulfill your delivery requests." },
    { n: "02", title: "Information We Collect", body: "When you book a delivery through LocalRush, we collect: your name and business name, pickup and dropoff addresses, sender and recipient phone numbers, your email address for order confirmations, package description and any special instructions you provide, and payment information which is processed and stored directly by our payment processor (we never see or store your full card number). We also collect basic technical information when you visit our website, including IP address, browser type, and pages visited — this is standard web server logging used for security and performance monitoring." },
    { n: "03", title: "How We Use Your Information", body: "We use your information strictly to: dispatch couriers for your delivery requests, send you and your recipients tracking notifications via SMS and email, process payment for your orders, respond to your customer support inquiries, maintain records for tax and accounting purposes as required by law, and improve our service. We do not use your information for marketing to third parties, and we do not build advertising profiles." },
    { n: "04", title: "Who We Share Information With", body: "To fulfill your delivery, we share limited information with third-party service providers: Stripe, Inc. (for processing payments — they receive your payment details, name, email, and delivery amount), our third-party courier network (for dispatching drivers — they receive pickup/dropoff addresses and phone numbers), Twilio, Inc. (if SMS notifications are enabled — they receive the phone numbers and message content), and Google Workspace and related infrastructure providers (for email delivery and hosting). Each of these providers has their own privacy practices which we've reviewed. We never sell your information to third parties for marketing or any other purpose." },
    { n: "05", title: "Payment Information and PCI Compliance", body: "All payment card information is collected directly by Stripe, Inc. through their secure Elements integration. LocalRush does not see, store, or have access to your full card number, security code, or expiration date at any time. Stripe is certified as a PCI Service Provider Level 1, which is the highest level of certification available. You can review Stripe's privacy policy at stripe.com/privacy." },
    { n: "06", title: "SMS and Communication", body: "By providing your phone number when booking a delivery, you consent to receive SMS messages from LocalRush and our dispatch partner regarding your specific delivery order — including pickup confirmations, driver assignment, status updates, and delivery confirmation. Recipients whose phone numbers you provide will also receive tracking notifications. Standard message and data rates from your mobile carrier may apply. We do not send marketing or promotional SMS. You can reply STOP to any automated SMS to opt out of further messages for that order." },
    { n: "07", title: "Data Retention", body: "We retain delivery records, including names, addresses, and transaction details, for seven years from the date of the delivery — this matches standard business record-keeping requirements for tax and accounting purposes. Technical server logs are retained for up to 90 days. Payment records held by Stripe are retained according to their own policies. If you request deletion of your information (see Your Rights below), we will delete or anonymize what we can while retaining what is legally required." },
    { n: "08", title: "Your Rights", body: "You have the right to: request a copy of the personal information we hold about you, request correction of inaccurate information, request deletion of your information (subject to legal retention requirements), opt out of any non-essential communications, and receive your information in a portable format. To exercise any of these rights, email us at kevin.localrush@gmail.com with a description of your request. We will respond within 30 days." },
    { n: "09", title: "California Residents (CCPA)", body: "If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with additional rights regarding your personal information. LocalRush does not sell personal information as defined by the CCPA. You have the right to request disclosure of the categories and specific pieces of personal information we have collected about you, and to request deletion subject to certain exceptions. Contact us at the email above to exercise these rights." },
    { n: "10", title: "European Residents (GDPR)", body: "If you are located in the European Economic Area, the UK, or Switzerland, the General Data Protection Regulation (GDPR) provides you with rights regarding your personal data. Our legal basis for processing your data is the performance of a contract (fulfilling your delivery) and our legitimate business interests (providing and improving the service). Note that LocalRush currently operates only in Salt Lake City, Utah, so EU-based users are unlikely to use our service — but these protections apply to anyone in the covered regions who does." },
    { n: "11", title: "Cookies and Tracking", body: "Our website uses minimal cookies. We use functional cookies required for the website to work correctly (such as maintaining your form state during checkout). We do not currently use advertising or third-party tracking cookies. Our payment processor Stripe may set cookies related to fraud prevention, which are necessary for processing payments securely. No behavioral advertising profiles are built from your visits to localrush.io." },
    { n: "12", title: "Children's Privacy", body: "LocalRush is a business-to-business delivery service and is not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it." },
    { n: "13", title: "Data Security", body: "We take reasonable measures to protect your information, including SSL/TLS encryption on our website, secure API connections with our payment and dispatch partners, access controls on our databases, and use of established third-party infrastructure providers (Railway, Netlify, Stripe) which maintain their own security certifications. However, no system is perfectly secure — if we become aware of a data breach affecting your personal information, we will notify you and relevant authorities as required by applicable law." },
    { n: "14", title: "Changes to This Policy", body: "We may update this Privacy Policy from time to time. When we do, we will update the effective date at the top of this page. For material changes, we will attempt to notify you via email if we have your address on file. Your continued use of LocalRush after changes take effect constitutes acceptance of the updated policy." },
    { n: "15", title: "Contact Us About Privacy", body: "If you have questions, concerns, or requests regarding this Privacy Policy or your personal information, please email kevin.localrush@gmail.com with the subject line \"Privacy Request.\" We aim to respond to all privacy-related inquiries within 5 business days and to formal rights requests within 30 days." },
  ];

  return (
    <div className="mobile-tight-padding-large" style={{ background: "var(--cream)", minHeight: "100vh", padding: "140px 40px 100px" }}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <div className="mono" style={{ fontSize: 11, color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 20, fontWeight: 500 }}>
          Legal
        </div>
        <h1 className="serif mobile-hero-title" style={{ fontSize: "clamp(48px, 6vw, 72px)", fontWeight: 400, color: "var(--ink)", letterSpacing: "-0.04em", lineHeight: 0.95, margin: "0 0 16px" }}>
          Privacy policy
        </h1>
        <p className="mono" style={{ color: "var(--ink-faint)", fontSize: 12, marginBottom: 56, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Effective April 17, 2026 · LocalRush · Salt Lake City, UT
        </p>

        <div style={{ background: "var(--orange-tint)", border: "1px solid rgba(242,92,18,0.2)", borderRadius: 12, padding: "18px 22px", marginBottom: 56 }}>
          <p style={{ color: "var(--ink-soft)", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: "var(--orange-deep)" }}>The short version:</strong> We collect only what we need to fulfill your deliveries. We share information only with the partners who help us do that (Stripe, our courier network, Twilio). We don't sell your data. Ever.
          </p>
        </div>

        {sections.map(({ n, title, body }) => (
          <div key={n} style={{ marginBottom: 40, paddingBottom: 40, borderBottom: "1px solid var(--line)" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 14 }}>
              <span className="mono" style={{ fontSize: 13, color: "var(--orange)", fontWeight: 500 }}>{n}</span>
              <h2 className="serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", margin: 0 }}>{title}</h2>
            </div>
            <p style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.7, margin: 0 }}>{body}</p>
          </div>
        ))}

        <div style={{ background: "var(--ink)", borderRadius: 14, padding: "28px", marginTop: 16 }}>
          <div className="serif" style={{ fontSize: 20, fontWeight: 500, color: "var(--cream)", letterSpacing: "-0.02em", marginBottom: 10 }}>
            Privacy questions? <span style={{ fontStyle: "italic", color: "var(--orange)" }}>Ask us.</span>
          </div>
          <a href="mailto:kevin.localrush@gmail.com" style={{ color: "var(--orange)", fontSize: 16, textDecoration: "none" }} className="ul-link">kevin.localrush@gmail.com</a>
          <div style={{ color: "rgba(250,247,242,0.5)", fontSize: 13, marginTop: 4 }}>Subject line: "Privacy Request" for fastest response</div>
        </div>
      </div>
    </div>
  );
}

function Footer({ setActive }) {
  return (
    <footer className="mobile-tight-padding" style={{ background: "var(--cream)", borderTop: "1px solid var(--line)", padding: "56px 40px 32px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="mobile-footer-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40, marginBottom: 48, flexWrap: "wrap" }}>
          <div>
            <Logo size={28} textSize={20} />
            <p className="serif" style={{ color: "var(--ink-muted)", fontSize: 15, margin: "16px 0 0", maxWidth: 320, lineHeight: 1.5, fontStyle: "italic" }}>
              Local delivery. Consider it done.
            </p>
          </div>

          <div>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontWeight: 500 }}>Site</div>
            {["Home", "Pricing", "About"].map(item => (
              <button key={item} onClick={() => setActive(item)} style={{
                display: "block", background: "transparent", border: "none",
                color: "var(--ink-soft)", padding: "4px 0",
                fontSize: 14, fontFamily: "'Geist', sans-serif",
                cursor: "pointer", textAlign: "left",
              }}>{item}</button>
            ))}
          </div>

          <div>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontWeight: 500 }}>Legal</div>
            <button onClick={() => setActive("Terms")} style={{
              display: "block", background: "transparent", border: "none",
              color: "var(--ink-soft)", padding: "4px 0", fontSize: 14, cursor: "pointer", textAlign: "left",
            }}>Terms of service</button>
            <button onClick={() => setActive("Privacy")} style={{
              display: "block", background: "transparent", border: "none",
              color: "var(--ink-soft)", padding: "4px 0", fontSize: 14, cursor: "pointer", textAlign: "left",
            }}>Privacy policy</button>
          </div>

          <div>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, fontWeight: 500 }}>Contact</div>
            <a href="mailto:kevin.localrush@gmail.com" style={{ display: "block", color: "var(--ink-soft)", fontSize: 14, textDecoration: "none", marginBottom: 6 }} className="ul-link">kevin.localrush@gmail.com</a>
            <a href="tel:9788558419" style={{ display: "block", color: "var(--ink-soft)", fontSize: 14, textDecoration: "none" }} className="ul-link">(978) 855-8419</a>
          </div>
        </div>

        <div style={{ borderTop: "1px solid var(--line)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div className="mono" style={{ color: "var(--ink-faint)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            © 2026 LocalRush · Salt Lake City, UT
          </div>
          <div className="mono" style={{ color: "var(--ink-faint)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Built in the valley
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [active, setActive] = useState("Home");
  useEffect(() => { window.scrollTo(0, 0); }, [active]);

  const pages = {
    "Home": <><HomePage setActive={setActive} /><Footer setActive={setActive} /></>,
    "Pricing": <><PricingPage setActive={setActive} /><Footer setActive={setActive} /></>,
    "About": <><AboutPage setActive={setActive} /><Footer setActive={setActive} /></>,
    "Book Now": <BookPage />,
    "Terms": <><TermsPage setActive={setActive} /><Footer setActive={setActive} /></>,
    "Privacy": <><PrivacyPage setActive={setActive} /><Footer setActive={setActive} /></>,
  };

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav active={active} setActive={setActive} />
      {pages[active]}
    </div>
  );
}
